/**
 * App-wide continuous corners.
 *
 * WHY THIS IS INJECTED AT RUNTIME: Tailwind v4 processes CSS through Lightning
 * CSS, which strips the `corner-shape` property it does not recognise. Any
 * `corner-shape` rule authored in a .css file is silently dropped from the
 * served stylesheet, so the browser supports the property
 * (`CSS.supports('corner-shape','squircle') === true` in Chromium 139+) but
 * never receives the declaration. Injecting a plain <style> at runtime
 * side-steps the build entirely. Every other browser ignores the property and
 * keeps a plain rounded corner, which is why the design has to work without
 * squircles and merely be better with them.
 *
 * WHY THE UNIVERSAL SELECTOR: `corner-shape` does NOT inherit. A parent set to
 * squircle yields children computed as `round`, so reaching every element
 * needs `*`. Measured over 40,000 elements this costs nothing: insert plus
 * style was 60-64ms with squircle against 82-86ms with round.
 *
 * WHY THE EXEMPTIONS ARE NOT OPTIONAL: `border-radius: 9999px` combined with
 * `corner-shape: squircle` renders a rounded SQUARE, not a circle. Verified by
 * screenshot and hit-test. Without the round exemption below, every avatar,
 * status dot, capsule button, `animate-ping` halo and decorative orb in the
 * app deforms, and the 26 div-based ring spinners
 * (`rounded-full` + `border-t-transparent` + `animate-spin`) become
 * hard-cornered "U" arcs that visibly wobble while spinning, on every loading
 * state in the product.
 *
 * Note `mask-image` overrides corner-shape entirely, so masked elements are
 * unaffected either way. `overflow: hidden`, `backdrop-filter`, `outline` and
 * `box-shadow` all clip and follow the squircle correctly.
 */
const STYLE_ID = 'ds-squircle-runtime'

const SQUIRCLE_CSS = `
*, *::before, *::after { corner-shape: squircle; }

/* ── Back to a true arc for everything whose shape IS a circle or a stadium ──
   Two groups, because a capsule can be declared two different ways.

   1. By utility class. Covers the ~1000 Tailwind call sites at once, and covers
      any avatar or pill written tomorrow without anyone remembering this file. */
.rounded-full,
[class*="rounded-full"],
[class*="radius-capsule"],
[class*="rounded-\\[9999px\\]"],
[class*="rounded-\\[50%\\]"],
[class*="animate-spin"],
[class*="animate-ping"],
[class*="rounded-full"]::before,
[class*="rounded-full"]::after,
svg, svg *,

/* 2. By stylesheet. These set a capsule or 50% radius in a .css file, so no
      class pattern can find them, and every one of them would otherwise render
      as a rounded square: range-slider tracks and thumbs, avatars, orbs, dots,
      scrollbar thumbs, the tab count badge.

      THIS LIST IS THE REGISTRY. A new CSS rule with border-radius 9999px or
      50% must be added here, or it will deform. Find them with:
        rg 'border-radius:\\s*(9999px|50%|var\\(--radius-full\\))' --glob '*.css' */
input[type="range"]::-webkit-slider-runnable-track,
input[type="range"]::-webkit-slider-thumb,
.ds-tab-count,
.ac-orb, .ac-ring,
.companion-bubble,
.companion-bubble--has-suggestion::before,
.companion-bubble--has-suggestion::after,
.companion-badge,
.cs-leader-avatar,
.fp-eq span,
.hl-aurora, .hl-glow, .hl-sun, .hl-sun-ring,
.hl-sun::before, .hl-sun::after,
.hl-eq span,
.hc-live-dot, .hc-wave span, .hc-booking-badge, .hc-ai-orb,
.ph-motion-dot, .ph-motion-tag, .ph-motion-ended, .ph-motion-voice, .ph-motion-orb-mini,
.ph-conversation-orb,
.ph-motion-count, .ph-motion-chip, .ph-motion-action-shape, .ph-motion-levels span,
.ph-motion-progress, .ph-motion-progress span, .ph-motion-live, .ph-motion-halo,
.ph-motion-ripple, .ph-motion-orb, .ph-motion-blob, .ph-motion-tap,
.hc-transcript::-webkit-scrollbar-thumb, .hc-dots span,
.ds-avatar--circle,
.ds-user-pill, .ds-user-pill::before, .ds-user-pill__identity, .ds-user-pill__remove,
.rli-avatar,
.set-mini-ava,
.cv-capsule { corner-shape: round; }

/* Firefox's range pseudo-elements get their OWN rule, never the list above.
   A comma-separated selector list is invalidated in full by a single selector
   the engine does not recognise, so mixing ::-webkit-* and ::-moz-* in one
   list silently kills the whole rule in both browsers. That exact mistake
   turned every avatar, status dot and ring spinner in the app into a rounded
   square, and it fails silently: the CSS parses, the rule is simply absent. */
input[type="range"]::-moz-range-track { corner-shape: round; }
input[type="range"]::-moz-range-thumb { corner-shape: round; }
input[type="range"]::-moz-range-progress { corner-shape: round; }

/* The tab count badge is a pill. The track and its travelling indicator are
   not: they are the control family, so they keep the squircle like every
   other rounded rectangle. */
.ds-tab-count { corner-shape: round; }

/* Surfaces that crop a moving video or photo to their own edge keep a true
   arc: under a squircle overflow clip, Chromium paints object-fit content
   about 5px past the right-hand edge. */
.ph-motion-call-tile, .ph-motion-grid-tile, .ph-motion-clip-thumb,
.ph-motion-player-video, .ph-motion-decision-media { corner-shape: round; }
`

export function ensureSquircleStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) {
    return
  }
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = SQUIRCLE_CSS
  document.head.appendChild(style)
}
